function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { DEFAULT_ENVIRONMENT } from '../constants.js';
import { getGlobalScope } from '../currentScopes.js';
import { notifyEventProcessors } from '../eventProcessors.js';
import { Scope } from '../scope.js';
import { getFilenameToDebugIdMap } from '../utils-hoist/debug-ids.js';
import { uuid4, addExceptionMechanism } from '../utils-hoist/misc.js';
import { normalize } from '../utils-hoist/normalize.js';
import { truncate } from '../utils-hoist/string.js';
import { dateTimestampInSeconds } from '../utils-hoist/time.js';
import { mergeScopeData, applyScopeDataToEvent } from './applyScopeDataToEvent.js';
/**
 * This type makes sure that we get either a CaptureContext, OR an EventHint.
 * It does not allow mixing them, which could lead to unexpected outcomes, e.g. this is disallowed:
 * { user: { id: '123' }, mechanism: { handled: false } }
 */

/**
 * Adds common information to events.
 *
 * The information includes release and environment from `options`,
 * breadcrumbs and context (extra, tags and user) from the scope.
 *
 * Information that is already present in the event is never overwritten. For
 * nested objects, such as the context, keys are merged.
 *
 * @param event The original event.
 * @param hint May contain additional information about the original exception.
 * @param scope A scope containing event metadata.
 * @returns A new event with more information.
 * @hidden
 */

function prepareEvent(options, event, hint, scope, client, isolationScope) {
  var {
    normalizeDepth = 3,
    normalizeMaxBreadth = 1000
  } = options;

  var prepared = _objectSpread(_objectSpread({}, event), {}, {
    event_id: event.event_id || hint.event_id || uuid4(),
    timestamp: event.timestamp || dateTimestampInSeconds()
  });

  var integrations = hint.integrations || options.integrations.map(i => i.name);
  applyClientOptions(prepared, options);
  applyIntegrationsMetadata(prepared, integrations);

  if (client) {
    client.emit('applyFrameMetadata', event);
  } // Only put debug IDs onto frames for error events.


  if (event.type === undefined) {
    applyDebugIds(prepared, options.stackParser);
  } // If we have scope given to us, use it as the base for further modifications.
  // This allows us to prevent unnecessary copying of data if `captureContext` is not provided.


  var finalScope = getFinalScope(scope, hint.captureContext);

  if (hint.mechanism) {
    addExceptionMechanism(prepared, hint.mechanism);
  }

  var clientEventProcessors = client ? client.getEventProcessors() : []; // This should be the last thing called, since we want that
  // {@link Scope.addEventProcessor} gets the finished prepared event.
  // Merge scope data together

  var data = getGlobalScope().getScopeData();

  if (isolationScope) {
    var isolationData = isolationScope.getScopeData();
    mergeScopeData(data, isolationData);
  }

  if (finalScope) {
    var finalScopeData = finalScope.getScopeData();
    mergeScopeData(data, finalScopeData);
  }

  var attachments = [...(hint.attachments || []), ...data.attachments];

  if (attachments.length) {
    hint.attachments = attachments;
  }

  applyScopeDataToEvent(prepared, data);
  var eventProcessors = [...clientEventProcessors, // Run scope event processors _after_ all other processors
  ...data.eventProcessors];
  var result = notifyEventProcessors(eventProcessors, prepared, hint);
  return result.then(evt => {
    if (evt) {
      // We apply the debug_meta field only after all event processors have ran, so that if any event processors modified
      // file names (e.g.the RewriteFrames integration) the filename -> debug ID relationship isn't destroyed.
      // This should not cause any PII issues, since we're only moving data that is already on the event and not adding
      // any new data
      applyDebugMeta(evt);
    }

    if (typeof normalizeDepth === 'number' && normalizeDepth > 0) {
      return normalizeEvent(evt, normalizeDepth, normalizeMaxBreadth);
    }

    return evt;
  });
}
/**
 * Enhances event using the client configuration.
 * It takes care of all "static" values like environment, release and `dist`,
 * as well as truncating overly long values.
 *
 * Only exported for tests.
 *
 * @param event event instance to be enhanced
 */


function applyClientOptions(event, options) {
  var _event$exception, _event$exception$valu;

  var {
    environment,
    release,
    dist,
    maxValueLength = 250
  } = options; // empty strings do not make sense for environment, release, and dist
  // so we handle them the same as if they were not provided

  event.environment = event.environment || environment || DEFAULT_ENVIRONMENT;

  if (!event.release && release) {
    event.release = release;
  }

  if (!event.dist && dist) {
    event.dist = dist;
  }

  if (event.message) {
    event.message = truncate(event.message, maxValueLength);
  }

  var exception = (_event$exception = event.exception) === null || _event$exception === void 0 ? void 0 : (_event$exception$valu = _event$exception.values) === null || _event$exception$valu === void 0 ? void 0 : _event$exception$valu[0];

  if (exception !== null && exception !== void 0 && exception.value) {
    exception.value = truncate(exception.value, maxValueLength);
  }

  var request = event.request;

  if (request !== null && request !== void 0 && request.url) {
    request.url = truncate(request.url, maxValueLength);
  }
}
/**
 * Puts debug IDs into the stack frames of an error event.
 */


function applyDebugIds(event, stackParser) {
  var _event$exception2, _event$exception2$val;

  // Build a map of filename -> debug_id
  var filenameDebugIdMap = getFilenameToDebugIdMap(stackParser);
  (_event$exception2 = event.exception) === null || _event$exception2 === void 0 ? void 0 : (_event$exception2$val = _event$exception2.values) === null || _event$exception2$val === void 0 ? void 0 : _event$exception2$val.forEach(exception => {
    var _exception$stacktrace, _exception$stacktrace2;

    (_exception$stacktrace = exception.stacktrace) === null || _exception$stacktrace === void 0 ? void 0 : (_exception$stacktrace2 = _exception$stacktrace.frames) === null || _exception$stacktrace2 === void 0 ? void 0 : _exception$stacktrace2.forEach(frame => {
      if (frame.filename) {
        frame.debug_id = filenameDebugIdMap[frame.filename];
      }
    });
  });
}
/**
 * Moves debug IDs from the stack frames of an error event into the debug_meta field.
 */


function applyDebugMeta(event) {
  var _event$exception3, _event$exception3$val;

  // Extract debug IDs and filenames from the stack frames on the event.
  var filenameDebugIdMap = {};
  (_event$exception3 = event.exception) === null || _event$exception3 === void 0 ? void 0 : (_event$exception3$val = _event$exception3.values) === null || _event$exception3$val === void 0 ? void 0 : _event$exception3$val.forEach(exception => {
    var _exception$stacktrace3, _exception$stacktrace4;

    (_exception$stacktrace3 = exception.stacktrace) === null || _exception$stacktrace3 === void 0 ? void 0 : (_exception$stacktrace4 = _exception$stacktrace3.frames) === null || _exception$stacktrace4 === void 0 ? void 0 : _exception$stacktrace4.forEach(frame => {
      if (frame.debug_id) {
        if (frame.abs_path) {
          filenameDebugIdMap[frame.abs_path] = frame.debug_id;
        } else if (frame.filename) {
          filenameDebugIdMap[frame.filename] = frame.debug_id;
        }

        delete frame.debug_id;
      }
    });
  });

  if (Object.keys(filenameDebugIdMap).length === 0) {
    return;
  } // Fill debug_meta information


  event.debug_meta = event.debug_meta || {};
  event.debug_meta.images = event.debug_meta.images || [];
  var images = event.debug_meta.images;
  Object.entries(filenameDebugIdMap).forEach(_ref => {
    var [filename, debug_id] = _ref;
    images.push({
      type: 'sourcemap',
      code_file: filename,
      debug_id
    });
  });
}
/**
 * This function adds all used integrations to the SDK info in the event.
 * @param event The event that will be filled with all integrations.
 */


function applyIntegrationsMetadata(event, integrationNames) {
  if (integrationNames.length > 0) {
    event.sdk = event.sdk || {};
    event.sdk.integrations = [...(event.sdk.integrations || []), ...integrationNames];
  }
}
/**
 * Applies `normalize` function on necessary `Event` attributes to make them safe for serialization.
 * Normalized keys:
 * - `breadcrumbs.data`
 * - `user`
 * - `contexts`
 * - `extra`
 * @param event Event
 * @returns Normalized event
 */


function normalizeEvent(event, depth, maxBreadth) {
  var _event$contexts, _event$contexts2;

  if (!event) {
    return null;
  }

  var normalized = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, event), event.breadcrumbs && {
    breadcrumbs: event.breadcrumbs.map(b => _objectSpread(_objectSpread({}, b), b.data && {
      data: normalize(b.data, depth, maxBreadth)
    }))
  }), event.user && {
    user: normalize(event.user, depth, maxBreadth)
  }), event.contexts && {
    contexts: normalize(event.contexts, depth, maxBreadth)
  }), event.extra && {
    extra: normalize(event.extra, depth, maxBreadth)
  }); // event.contexts.trace stores information about a Transaction. Similarly,
  // event.spans[] stores information about child Spans. Given that a
  // Transaction is conceptually a Span, normalization should apply to both
  // Transactions and Spans consistently.
  // For now the decision is to skip normalization of Transactions and Spans,
  // so this block overwrites the normalized event to add back the original
  // Transaction information prior to normalization.


  if ((_event$contexts = event.contexts) !== null && _event$contexts !== void 0 && _event$contexts.trace && normalized.contexts) {
    normalized.contexts.trace = event.contexts.trace; // event.contexts.trace.data may contain circular/dangerous data so we need to normalize it

    if (event.contexts.trace.data) {
      normalized.contexts.trace.data = normalize(event.contexts.trace.data, depth, maxBreadth);
    }
  } // event.spans[].data may contain circular/dangerous data so we need to normalize it


  if (event.spans) {
    normalized.spans = event.spans.map(span => {
      return _objectSpread(_objectSpread({}, span), span.data && {
        data: normalize(span.data, depth, maxBreadth)
      });
    });
  } // event.contexts.flags (FeatureFlagContext) stores context for our feature
  // flag integrations. It has a greater nesting depth than our other typed
  // Contexts, so we re-normalize with a fixed depth of 3 here. We do not want
  // to skip this in case of conflicting, user-provided context.


  if ((_event$contexts2 = event.contexts) !== null && _event$contexts2 !== void 0 && _event$contexts2.flags && normalized.contexts) {
    normalized.contexts.flags = normalize(event.contexts.flags, 3, maxBreadth);
  }

  return normalized;
}

function getFinalScope(scope, captureContext) {
  if (!captureContext) {
    return scope;
  }

  var finalScope = scope ? scope.clone() : new Scope();
  finalScope.update(captureContext);
  return finalScope;
}
/**
 * Parse either an `EventHint` directly, or convert a `CaptureContext` to an `EventHint`.
 * This is used to allow to update method signatures that used to accept a `CaptureContext` but should now accept an `EventHint`.
 */


function parseEventHintOrCaptureContext(hint) {
  if (!hint) {
    return undefined;
  } // If you pass a Scope or `() => Scope` as CaptureContext, we just return this as captureContext


  if (hintIsScopeOrFunction(hint)) {
    return {
      captureContext: hint
    };
  }

  if (hintIsScopeContext(hint)) {
    return {
      captureContext: hint
    };
  }

  return hint;
}

function hintIsScopeOrFunction(hint) {
  return hint instanceof Scope || typeof hint === 'function';
}

var captureContextKeys = ['user', 'level', 'extra', 'contexts', 'tags', 'fingerprint', 'propagationContext'];

function hintIsScopeContext(hint) {
  return Object.keys(hint).some(key => captureContextKeys.includes(key));
}

export { applyClientOptions, applyDebugIds, applyDebugMeta, parseEventHintOrCaptureContext, prepareEvent };